import { createFileRoute, Link } from '@tanstack/react-router'

import { Dancefloor } from '@/components/dancefloor'
import { useTwitchConnection } from '@/components/twitch/use-twitch-connection'
import { InlineError, SectionCard, Skeleton } from '@/components/ui'
import { useViewAsStore } from '@/stores/view-as-store'
import { apiClient } from '@/lib/api-client'

export const Route = createFileRoute('/admin/dancefloor')({
  component: AdminDancefloorPage,
})

function AdminDancefloorPage() {
  const viewAsId = useViewAsStore((state) => state.viewAsId)
  const connection = useTwitchConnection(viewAsId, 1000)
  const feed = connection.data?.feed
  const account = connection.data?.connection
  const login = account?.login ?? feed?.login
  const feedStatus = account?.state === 'needs_chat_permission' || account?.state === 'waiting_for_bot'
    ? `Twitch account @${login} is connected. ${connection.data?.can_manage
      ? 'Open Twitch chat settings and use Reconnect Twitch to allow LiveVane to read chat with this account. No separate bot account is needed.'
      : 'The account owner needs to reconnect Twitch to allow LiveVane to read chat.'}`
    : account?.state === 'paused'
      ? `Twitch account @${login} is connected, but its chat feed is paused. Enable the chat feed in Twitch chat settings.`
      : account?.state === 'reconnect'
        ? 'Twitch authorization needs to be renewed. Reconnect your account in Twitch chat settings.'
        : login
          ? `Twitch account @${login} is connected. Its chat feed is not ready yet.`
          : 'Connect your Twitch account in Twitch chat settings to let the dancefloor follow chat.'

  return (
    <div data-testid="admin-dancefloor">
      <SectionCard
        title={login ? `@${login} · Dancefloor` : 'Twitch dancefloor'}
        description="Guest commands and crowd controls are below the floor."
      >
        {connection.isLoading ? (
          <Skeleton className="aspect-video min-h-[360px] w-full" />
        ) : connection.isError ? (
          <InlineError message="Could not load the Twitch chat feed. Retrying…" />
        ) : (
          <div className="space-y-4">
            {!feed?.available && (
              <div
                data-testid="admin-dancefloor-unavailable"
                className="rounded-md border border-dashed border-border/40 px-4 py-8 text-center"
              >
                <p className="text-sm text-muted-foreground">
                  {feedStatus}{' '}
                  <Link to="/admin/chat" className="text-cyber-cyan underline underline-offset-4">
                    Open Twitch chat settings
                  </Link>
                  .
                </p>
              </div>
            )}
            {/* Broadcast settings remain editable even while Twitch chat is unavailable. */}
            {feed && (
              <Dancefloor
                key={`${viewAsId ?? 'self'}-${login}`}
                messages={feed.messages}
                presence={feed.presence}
                available={feed.available}
                login={login ?? undefined}
                broadcastPreferences={feed.dancefloor ?? { set: 'set-one' }}
                preferencesReadOnly={!connection.data?.can_manage}
                canHideCommands={connection.data?.connection.permissions?.delete ?? false}
                onPreferencesChange={async preferences => {
                  await apiClient.post('/twitch/', { action: 'dancefloor_settings', set: preferences.set, avatar: preferences.avatar ?? null,
                    demo_crowd: preferences.demo_crowd ?? false, hide_commands: preferences.hide_commands ?? false, ...(preferences.camera ? { camera: preferences.camera } : {}) })
                  await connection.refetch()
                }}
              />
            )}
          </div>
        )}
      </SectionCard>
    </div>
  )
}
